import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { solicitarAvisosAposSucesso } from '@/lib/avisosSucesso';
import { formatarCentavos, formatarMmSs } from '@/pages/loja/utils';
import Card from '@/components/ui/Card';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import type { PedidoIngressoPublico, PixIngresso } from '../types';

const INTERVALO_POLLING_MS = 4000;
const TEMPO_PIX_MS = 30 * 60 * 1000;

type Estado = 'carregando' | 'aguardando' | 'pago' | 'cancelado' | 'nao_encontrado' | 'erro';

export default function IngressoPedidoPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const eventoImagem =
    (location.state as { eventoImagem?: string | null } | null)?.eventoImagem ?? null;

  const [estado, setEstado] = useState<Estado>('carregando');
  const [pedido, setPedido] = useState<PedidoIngressoPublico | null>(null);
  const [pix, setPix] = useState<PixIngresso | null>(null);
  const [pixErro, setPixErro] = useState<string | null>(null);
  const [gerandoPix, setGerandoPix] = useState(false);
  const [expiraEm, setExpiraEm] = useState<number | null>(null);
  const [pixExpirado, setPixExpirado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [refazendo, setRefazendo] = useState(false);
  const [agora, setAgora] = useState(Date.now());

  const pollRef = useRef<number | null>(null);
  const avisosRef = useRef(false);

  async function carregarPedido(): Promise<PedidoIngressoPublico | null> {
    const { data, error } = await supabase.rpc('pedido_publico', { p_codigo: codigo });
    if (error) throw error;
    return (Array.isArray(data) ? data[0] : data) as PedidoIngressoPublico | null;
  }

  async function gerarPix() {
    setGerandoPix(true);
    setPixErro(null);
    setPixExpirado(false);
    try {
      const { data, error } = await supabase.functions.invoke('criar-pix-ingresso', {
        body: { pedido_codigo: codigo },
      });
      if (error) {
        setPixErro(
          typeof error === 'string'
            ? error
            : ((error as { message?: string })?.message || 'Não conseguimos gerar o Pix.'),
        );
        return;
      }
      const res = data as PixIngresso | null;
      if (!res?.pix_copia_e_cola) {
        setPixErro('Não conseguimos gerar o Pix. Tente de novo.');
        return;
      }
      setPix(res);
      const expMs = res.expira_em ? new Date(res.expira_em).getTime() : NaN;
      setExpiraEm(Number.isFinite(expMs) ? expMs : Date.now() + TEMPO_PIX_MS);
    } catch (e) {
      setPixErro(mensagemAmigavel(e, 'Não conseguimos gerar o Pix. Tente de novo.'));
    } finally {
      setGerandoPix(false);
    }
  }

  // Carga inicial.
  useEffect(() => {
    let ativo = true;
    async function iniciar() {
      setEstado('carregando');
      setErro(null);
      try {
        const p = await carregarPedido();
        if (!ativo) return;
        if (!p) {
          setEstado('nao_encontrado');
          return;
        }
        setPedido(p);
        if (p.status === 'pago') {
          setEstado('pago');
          return;
        }
        if (p.status === 'cancelado' || p.status === 'expirado') {
          setEstado('cancelado');
          return;
        }
        setEstado('aguardando');
        gerarPix();
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar o pedido.'));
          setEstado('erro');
        }
      }
    }
    iniciar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo]);

  // Polling do status do pedido.
  useEffect(() => {
    if (estado !== 'aguardando') return;
    const checar = async () => {
      try {
        const p = await carregarPedido();
        if (!p) return;
        setPedido(p);
        if (p.status === 'pago') setEstado('pago');
        else if (p.status === 'cancelado' || p.status === 'expirado') setEstado('cancelado');
      } catch {
        /* silencioso: próximo tick tenta de novo */
      }
    };
    pollRef.current = window.setInterval(checar, INTERVALO_POLLING_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);

  // Detecta expiração do Pix e atualiza o relógio a cada segundo.
  useEffect(() => {
    if (estado !== 'aguardando' || !expiraEm || pixExpirado) return;
    const checar = () => {
      setAgora(Date.now());
      if (Date.now() >= expiraEm) setPixExpirado(true);
    };
    checar();
    const id = window.setInterval(checar, 1000);
    return () => window.clearInterval(id);
  }, [estado, expiraEm, pixExpirado]);

  // Ao pagar, navega para a carteira.
  useEffect(() => {
    if (estado !== 'pago' || !pedido?.carteira_codigo) return;
    const t = setTimeout(() => {
      navigate(`/carteira/${pedido.carteira_codigo}`, { replace: true });
    }, 1200);
    return () => clearTimeout(t);
  }, [estado, pedido, navigate]);

  // Pix confirmado: uma vibrada curta, quando o aparelho permite.
  useEffect(() => {
    if (estado !== 'pago') return;
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate?.(50);
  }, [estado]);

  // Pagamento confirmado: pede os avisos no celular (1,5 s depois), uma vez só.
  useEffect(() => {
    if (estado !== 'pago' || avisosRef.current) return;
    avisosRef.current = true;
    solicitarAvisosAposSucesso(pedido?.evento_nome ?? undefined);
  }, [estado, pedido]);

  async function copiarCodigo() {
    if (!pix?.pix_copia_e_cola) return;
    try {
      await navigator.clipboard.writeText(pix.pix_copia_e_cola);
    } catch {
      /* não bloqueia */
    }
    setCopiado(true);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate?.(30);
    setTimeout(() => setCopiado(false), 2000);
  }

  async function refazerPedido() {
    setRefazendo(true);
    setErro(null);
    try {
      setPixExpirado(false);
      await gerarPix();
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos refazer o pedido.'));
    } finally {
      setRefazendo(false);
    }
  }

  async function cancelarPedido() {
    try {
      await supabase.rpc('cancelar_pedido_publico', { p_codigo: codigo });
    } catch {
      /* não bloqueia */
    }
    setEstado('cancelado');
  }

  // ---- Estados de erro / não encontrado / cancelado ----
  if (estado === 'nao_encontrado' || estado === 'erro') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i
              className={estado === 'erro' ? 'ri-error-warning-line text-[40px]' : 'ri-search-line text-[40px]'}
              aria-hidden
            />
          </span>
          <p className="mt-3 text-[15px] text-foreground-700">
            {estado === 'erro' ? erro || 'Não foi possível carregar.' : 'Pedido não encontrado.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white"
          >
            Voltar à vitrine
          </button>
        </Card>
      </div>
    );
  }

  if (estado === 'cancelado') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-time-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-3 text-[20px] font-extrabold text-foreground-950">Pedido expirado</h1>
          <p className="mt-2 text-[14px] text-foreground-600">
            O prazo de pagamento terminou. Você pode refazer o pedido com os mesmos dados.
          </p>
          <button
            type="button"
            onClick={refazerPedido}
            disabled={refazendo}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white disabled:opacity-60"
          >
            {refazendo ? 'Refazendo…' : 'Refazer pedido'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="aperta mt-2 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] border border-background-200 bg-background-50 text-[15px] font-semibold text-foreground-800"
          >
            Voltar à vitrine
          </button>
        </Card>
      </div>
    );
  }

  // ---- Pagamento confirmado (com animação de check) ----
  if (estado === 'pago') {
    return (
      <div className="min-h-screen bg-background-100">
        <button
          type="button"
          onClick={() => {
            if (!pedido?.carteira_codigo) return;
            navigate(`/carteira/${pedido.carteira_codigo}`, { replace: true });
          }}
          className="flex min-h-screen w-full cursor-pointer flex-col items-center justify-center px-5 text-center"
        >
          <CheckAnimado tamanho={96} />
          <h1 className="mt-4 text-[24px] font-extrabold text-foreground-950">Pix confirmado!</h1>
          <p className="mt-1 text-[14px] text-foreground-600">Abrindo seus ingressos…</p>
        </button>
      </div>
    );
  }

  // ---- Aguardando Pix ----
  const restanteMs = expiraEm ? Math.max(0, expiraEm - agora) : TEMPO_PIX_MS;
  const critico = expiraEm != null && restanteMs < 5 * 60 * 1000;

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[560px] px-5 py-6">
        <div className="text-center">
          <p className="text-[13px] font-medium text-foreground-500">Falta só pagar</p>
          {pedido && (
            <p className="mt-1 text-[34px] font-extrabold leading-none text-foreground-950">
              {formatarCentavos(pedido.total_centavos)}
            </p>
          )}
          {pedido?.comprador && (
            <p className="mt-1 text-[13px] text-foreground-500">Pedido de {pedido.comprador}</p>
          )}
        </div>

        {pixErro && (
          <Card className="mt-5 p-4">
            <p className="text-[14px] text-danger-700">{pixErro}</p>
            <button
              type="button"
              onClick={gerarPix}
              disabled={gerandoPix}
              className="aperta mt-3 h-12 w-full cursor-pointer whitespace-nowrap rounded-[14px] border border-background-200 bg-background-50 text-[14px] font-semibold text-foreground-800 disabled:opacity-60"
            >
              {gerandoPix ? 'Gerando…' : 'Tentar de novo'}
            </button>
          </Card>
        )}

        {!pix && !pixErro && (
          <Card className="mt-5 p-8 text-center">
            <span className="mx-auto flex h-10 w-10 items-center justify-center text-primary-500">
              <i className="ri-loader-4-line animate-spin text-3xl" aria-hidden />
            </span>
            <p className="mt-3 text-[14px] text-foreground-600">Gerando seu Pix…</p>
          </Card>
        )}

        {pix && !pixExpirado && (
          <>
            <Card className="mt-5 p-5">
              <div className="flex justify-center">
                <div className="qr-respira rounded-[18px] border border-background-200 bg-white p-3">
                  <img
                    src={`data:image/png;base64,${pix.pix_qrcode_base64}`}
                    alt="QR Code Pix"
                    width={256}
                    height={256}
                  />
                </div>
              </div>
              <p className="mt-3 text-center text-[13px] text-foreground-500">
                Abra o app do seu banco → Pix → Ler QR Code
              </p>

              <button
                type="button"
                onClick={copiarCodigo}
                className={`aperta mt-4 flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[18px] text-[15px] font-bold text-white transition-colors ${
                  copiado ? 'bg-success-500' : 'bg-primary-500 hover:bg-primary-600'
                }`}
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
                </span>
                {copiado ? 'Código copiado!' : 'Copiar código Pix'}
              </button>

              <div
                className={`mt-3 flex items-center justify-center gap-1.5 text-[12px] ${
                  critico ? 'font-semibold text-warning-600' : 'text-foreground-400'
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-time-line" aria-hidden />
                </span>
                Vale por {formatarMmSs(restanteMs)}
              </div>
            </Card>

            {pix.detalhe && pix.detalhe.length > 0 && (
              <Card className="mt-4 p-5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400">
                  O que você vai pagar
                </p>
                <div className="mt-3 space-y-1.5">
                  {pix.detalhe.map((l, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between text-[14px] ${
                        l.destaque
                          ? 'border-t border-background-200 pt-2 font-bold text-foreground-950'
                          : 'text-foreground-700'
                      }`}
                    >
                      <span className="truncate">{l.linha}</span>
                      <span className="ml-3 whitespace-nowrap">{l.valor}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <div className="mt-5 flex items-center justify-center gap-2 text-[13px] text-foreground-600">
              <span className="relative flex h-2.5 w-2.5">
                <span
                  className="absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75"
                  style={{ animation: 'aguardandoPulso 2s ease-in-out infinite' }}
                />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success-500" />
              </span>
              Aguardando o seu Pix…
            </div>
          </>
        )}

        {pixExpirado && (
          <Card className="mt-5 p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning-50 text-warning-600">
              <i className="ri-time-line text-2xl" aria-hidden />
            </span>
            <h2 className="mt-3 text-[18px] font-extrabold text-foreground-950">Pix venceu</h2>
            <p className="mt-1 text-[14px] text-foreground-600">
              O código expirou. Você pode refazer o pedido com os mesmos dados.
            </p>
            {erro && <p className="mt-2 text-[14px] text-danger-700">{erro}</p>}
            <button
              type="button"
              onClick={refazerPedido}
              disabled={refazendo}
              className="aperta mt-5 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white disabled:opacity-60"
            >
              {refazendo ? 'Refazendo…' : 'Refazer pedido'}
            </button>
            <button
              type="button"
              onClick={cancelarPedido}
              className="aperta mt-2 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] border border-background-200 bg-background-50 text-[15px] font-semibold text-foreground-800"
            >
              Cancelar pedido
            </button>
          </Card>
        )}
      </div>

      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes aguardandoPulso {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 1; }
          }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes aguardandoPulso { 0%, 100% { opacity: 1; } }
        }
      `}</style>
    </div>
  );
}